import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft } from 'lucide-react';

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Header */}
      <div className="bg-slate-900/50 backdrop-blur-sm border-b border-slate-700/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center mb-6">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/')}
              className="mr-4 border-slate-600 text-white hover:bg-slate-800"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </div>
          
          <div className="text-center">
            <h1 className="text-4xl font-bold text-white mb-4">
              Terms of Service
            </h1>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              These terms govern your use of Flamingo and outline the rights and responsibilities of all users.
            </p>
            <p className="text-sm text-slate-400 mt-4">
              Last updated: January 2024
            </p>
          </div>
        </div>
      </div>

      {/* Terms Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Card className="p-8 bg-slate-800/50 backdrop-blur-sm border-slate-700/50">
          <div className="prose max-w-none text-slate-300">
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">Acceptance of Terms</h2>
              <p className="text-slate-300 mb-4">
                By accessing and using Flamingo, you accept and agree to be bound by the terms 
                and provision of this agreement. If you do not agree to these terms, you should 
                not use this service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">Use of Service</h2>
              <p className="text-slate-300 mb-4">
                Flamingo is a platform that connects renters and landlords. You agree to use 
                our service only for lawful purposes and in accordance with these terms.
              </p>
              <h3 className="text-lg font-semibold text-white mb-2">You agree NOT to:</h3>
              <ul className="list-disc list-inside text-slate-300 space-y-2 mb-4">
                <li>Post false, misleading, or fraudulent listings</li>
                <li>Harass, abuse, or harm other users</li>
                <li>Violate any laws or regulations</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Use automated tools to access our service</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">User Accounts</h2>
              <p className="text-slate-300 mb-4">
                To access certain features, you must create an account. You are responsible for:
              </p>
              <ul className="list-disc list-inside text-slate-300 space-y-2 mb-4">
                <li>Maintaining the security of your account</li>
                <li>All activities that occur under your account</li>
                <li>Providing accurate and current information</li>
                <li>Notifying us of any unauthorized use</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">Property Listings</h2>
              <p className="text-slate-300 mb-4">For landlords listing properties:</p>
              <ul className="list-disc list-inside text-slate-300 space-y-2 mb-4">
                <li>All listings must be accurate and truthful</li>
                <li>You must have legal authority to rent the property</li>
                <li>Photos must be current and represent the actual property</li>
                <li>Comply with all applicable fair housing laws</li>
                <li>Respond to inquiries in a timely manner</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">Payment Terms</h2>
              <p className="text-slate-300 mb-4">
                Flamingo is free for renters. Landlords pay a monthly subscription fee for 
                listing properties and accessing premium features.
              </p>
              <ul className="list-disc list-inside text-slate-300 space-y-2 mb-4">
                <li>Subscription fees are billed monthly</li>
                <li>No refunds for partial months</li>
                <li>You may cancel your subscription at any time</li>
                <li>All fees are subject to applicable taxes</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">Limitation of Liability</h2>
              <p className="text-slate-300 mb-4">
                Flamingo provides a platform to connect renters and landlords. We are not 
                responsible for:
              </p>
              <ul className="list-disc list-inside text-slate-300 space-y-2 mb-4">
                <li>The quality, safety, or legality of properties listed</li>
                <li>The truth or accuracy of listings</li>
                <li>The performance of rental agreements</li>
                <li>Disputes between renters and landlords</li>
                <li>Any damages arising from use of our service</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">Termination</h2>
              <p className="text-slate-300 mb-4">
                We may terminate or suspend your account immediately, without prior notice, 
                for conduct that we believe violates these terms or is harmful to other users.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">Changes to Terms</h2>
              <p className="text-slate-300 mb-4">
                We reserve the right to modify these terms at any time. We will notify users 
                of significant changes via email or platform notifications.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">Contact Information</h2>
              <p className="text-slate-300">
                Questions about these Terms of Service should be sent to us at 
                legal@flamingolisting.com or through our Contact Us page.
              </p>
            </section>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default TermsOfService;
